import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PROGRESS_SHOTS } from '../data/appData';

function ShotCard({ label, accent, image }) {
  return (
    <div className="shot-card" style={{ borderColor: accent }}>
      <div className="shot-preview" style={image ? { backgroundImage: `url(${image})` } : undefined} />
      <span className="shot-label">{label}</span>
    </div>
  );
}

// ------------------------------------------
// Canvas Helper: Generate Story Image
// ------------------------------------------
async function generateStoryImage(bgImageSrc, stats) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = "anonymous"; // Try to avoid taint issues
    img.src = bgImageSrc;

    img.onload = () => {
      // 1. Set Canvas to Story Aspect Ratio (9:16) roughly
      const width = 1080;
      const height = 1920;
      canvas.width = width;
      canvas.height = height;

      // 2. Draw Background Image (Cover fit)
      // Simple center crop logic
      let sWidth = img.width;
      let sHeight = img.height;
      let sx = 0;
      let sy = 0;
      const aspect = sWidth / sHeight;
      const targetAspect = width / height; // 9:16

      if (aspect > targetAspect) {
        // Image is wider than canvas
        sWidth = sHeight * targetAspect;
        sx = (img.width - sWidth) / 2;
      } else {
        // Image is taller than canvas
        sHeight = sWidth / targetAspect;
        sy = (img.height - sHeight) / 2;
      }

      // Draw clean background
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, width, height);

      // 3. Draw Gradient Overlay (Dark at bottom)
      const gradient = ctx.createLinearGradient(0, height * 0.5, 0, height);
      gradient.addColorStop(0, 'rgba(0,0,0,0)');
      gradient.addColorStop(0.8, 'rgba(0,0,0,0.9)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, height * 0.5, width, height * 0.5);

      // 4. Branding (Top)
      ctx.fillStyle = '#00e5ff'; // Cyan brand color
      ctx.font = 'bold 60px sans-serif';
      ctx.textAlign = 'center';
      ctx.shadowColor = "rgba(0,0,0,0.5)";
      ctx.shadowBlur = 10;
      ctx.fillText('SPORTIVOS', width / 2, 120);

      // 5. Visual Tag (User Request: @sportivosgym)
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 40px sans-serif';
      ctx.fillText('@sportivosgym', width / 2, 180);
      ctx.shadowBlur = 0; // Reset shadow

      // 6. Stats & Info (Bottom)
      ctx.textAlign = 'left';

      // "My Progress"
      ctx.fillStyle = '#ffffff';
      ctx.font = 'italic bold 80px sans-serif';
      ctx.fillText('MI PROGRESO 🔥', 60, height - 250);

      // Details
      ctx.fillStyle = '#00e5ff';
      ctx.font = '40px sans-serif';
      ctx.fillText(`${stats.label || 'Semana X'} · Nivel 5`, 60, height - 190);

      // Date
      const dateStr = new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
      ctx.fillStyle = '#aaaaaa';
      ctx.font = '30px sans-serif';
      ctx.fillText(dateStr, 60, height - 140);

      // 7. Export as Blob
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas blob failed'));
      }, 'image/png');
    };
    img.onerror = (err) => reject(err);
  });
}

function ProfileView({ profile, ranks, onEdit, onOpenSettings }) {
  const initials = profile.name ? profile.name.charAt(0).toUpperCase() : 'S';
  const currentRank = ranks.find((rank) => rank.id === profile.rankId);
  const fileInputRef = useRef(null);
  const [capturedShots, setCapturedShots] = useState([]);
  const [storyOpen, setStoryOpen] = useState(false);
  const [activeShot, setActiveShot] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    return () => {
      capturedShots.forEach((shot) => {
        if (shot.revokeUrl && typeof URL.revokeObjectURL === 'function') {
          URL.revokeObjectURL(shot.image);
        }
      });
    };
  }, [capturedShots]);

  const shots = useMemo(() => [...PROGRESS_SHOTS, ...capturedShots], [capturedShots]);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const imageUrl = URL.createObjectURL(file);
    const newShot = {
      id: `captured-${Date.now()}`,
      label: `Semana ${shots.length + 8}`,
      accent: '#38bdf8',
      image: imageUrl,
      revokeUrl: true,
    };
    setCapturedShots((prev) => [...prev, newShot]);
    event.target.value = '';
  };

  const openStoryMode = (shot) => {
    setActiveShot(shot);
    setStoryOpen(true);
  };

  const closeStoryMode = () => {
    setStoryOpen(false);
    setActiveShot(null);
  };

  const handleShareInstagram = async () => {
    if (!activeShot) return;
    setIsGenerating(true);

    try {
      const blob = await generateStoryImage(activeShot.image, activeShot);
      const file = new File([blob], 'sportivos-story.png', { type: 'image/png' });

      if (navigator.share) {
        await navigator.share({
          files: [file],
          title: 'Mi progreso en Sportivos Gym',
          text: 'Entrenando duro en @sportivosgym 🔥',
        });
        // Success feedback usually handled by OS, but we can alert briefly if needed
      } else {
        // Fallback for desktop: Download
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'sportivos-story.png';
        a.click();
        alert("Imagen descargada. ¡Súbela a tu historia!");
      }
    } catch (error) {
      console.error("Error sharing:", error);
      alert("Hubo un problema generando la imagen. Intenta de nuevo.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="screen">
      <section className="section">
        <div className="profile-summary">
          <div className={profile.avatarUrl ? 'avatar-circle avatar-circle-photo profile-avatar-large' : 'avatar-circle profile-avatar-large'}>
            {profile.avatarUrl ? <img src={profile.avatarUrl} alt={profile.name} /> : initials}
          </div>
          <div className="profile-info">
            <h1 className="title profile-title-main">{profile.name}</h1>
            {currentRank && <p className="profile-rank">{currentRank.label}</p>}
            <div className="profile-actions">
              <button type="button" className="pill-button pill-button-ghost" onClick={onEdit}>
                Editar
              </button>
              <button
                type="button"
                className="icon-button profile-settings"
                onClick={onOpenSettings}
                aria-label="Configuración"
              >
                ⚙️
              </button>
            </div>
          </div>
        </div>

        {/* Redesigned Glass Stats */}
        <div className="profile-stats-grid">
          <div className="stat-card-glass">
            <span className="stat-icon">🏅</span>
            <span className="stat-value">18</span>
            <span className="stat-label">Medallas</span>
          </div>
          <div className="stat-card-glass">
            <span className="stat-icon">🔥</span>
            <span className="stat-value stat-highlight">32</span>
            <span className="stat-label">Mix Semana</span>
          </div>
          <div className="stat-card-glass">
            <span className="stat-icon">📸</span>
            <span className="stat-value">{shots.length}</span>
            <span className="stat-label">Clips</span>
          </div>
        </div>
      </section>

      {/* Transformation Gallery */}
      <section className="section">
        <header className="section-header">
          <h2 className="section-title">Tu Evolución</h2>
          <button type="button" className="link-button" onClick={handleUploadClick}>+ Nuevo registro</button>
          <input
            id="shot-upload-input"
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </header>

        <div className="gallery-grid">
          {shots.map((shot) => (
            <div key={shot.id} className="polaroid-card" onClick={() => openStoryMode(shot)}>
              <img src={shot.image} alt="Progress" className="polaroid-img" />
              <div className="polaroid-caption">{shot.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Instagram Story Preview Modal */}
      {storyOpen && activeShot && (
        <div className="story-overlay" onClick={closeStoryMode}>
          <div className="story-preview" onClick={(e) => e.stopPropagation()}>
            <img src={activeShot.image} alt="Story" className="story-image" />

            {/* Overlays (Visual Preview only, real burn happens in generator) */}
            <div className="story-brand-overlay">@sportivosgym</div>

            <div className="story-stats-overlay">
              <h2 className="story-title">Mi Progreso 🔥</h2>
              <span className="story-tag">{activeShot.label} · Atleta Nivel 5</span>
            </div>
          </div>

          <div className="story-actions" onClick={(e) => e.stopPropagation()}>
            <button className="pill-button pill-button-ghost" style={{ color: 'white', borderColor: 'white' }} onClick={closeStoryMode}>Cancelar</button>
            <button className="pill-button pill-button-primary" onClick={handleShareInstagram} disabled={isGenerating}>
              {isGenerating ? 'Generando...' : 'Compartir en Instagram 📸'}
            </button>
          </div>
        </div>
      )}

      <section className="section">
        <div className="share-card">
          <div>
            <h3 className="share-title">Comparte tu avance</h3>
            <p className="share-text">Motiva a otros atletas subiendo tus logros.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ProfileView;
